import { Button, Flex, Input, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react'
import type { ItemResponseDto } from '@wanda-space/types'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { StorageItemList, itemMatchesSearchQuery } from './StorageItemList'

interface ItemsTabsProps {
  activeItems: ItemResponseDto[]
  inactiveItems: ItemResponseDto[]
  activeItemsEmptyStateBoxProps?: {
    titleLokalizeId?: string
    iconImgSrc?: string
    linkTo: string
    linkTextLokalizeId?: string
  }
}

export const ItemsTabs = ({
  activeItems,
  inactiveItems,
  activeItemsEmptyStateBoxProps,
}: ItemsTabsProps) => {
  const { formatMessage } = useIntl()
  const [searchQuery, setSearchQuery] = useState('')
  const [tabIndex, setTabIndex] = useState(0)

  const isSearching = searchQuery.trim().length > 0

  const visibleActiveItems = isSearching
    ? activeItems.filter((item) => itemMatchesSearchQuery(item, searchQuery))
    : activeItems
  const visibleInactiveItems = isSearching
    ? inactiveItems.filter((item) => itemMatchesSearchQuery(item, searchQuery))
    : inactiveItems

  useEffect(() => {
    if (!isSearching) {
      return
    }
    setTabIndex((currentIndex) => {
      if (
        currentIndex === 0 &&
        visibleActiveItems.length === 0 &&
        visibleInactiveItems.length > 0
      ) {
        return 1
      }
      if (
        currentIndex === 1 &&
        visibleInactiveItems.length === 0 &&
        visibleActiveItems.length > 0
      ) {
        return 0
      }
      return currentIndex
    })
  }, [isSearching, visibleActiveItems.length, visibleInactiveItems.length])

  return (
    <>
      <Flex mb={4} alignItems="center" gap={2}>
        <Input
          placeholder={formatMessage({
            id: 'space.myItems.search.placeholder',
            defaultMessage: 'Søk etter ting...',
          })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {isSearching && (
          <Button
            variant="ghost"
            size="sm"
            textDecoration="underline"
            onClick={() => setSearchQuery('')}
          >
            {formatMessage({ id: 'word.reset' })}
          </Button>
        )}
      </Flex>

      <Tabs colorScheme="purple" index={tabIndex} onChange={setTabIndex}>
        <TabList>
          <Tab>
            {formatMessage(
              {
                id: 'space.myItems.activeItems.title',
                defaultMessage: 'Ting hos oss ({amountOfItems})',
              },
              { amountOfItems: visibleActiveItems.length }
            )}
          </Tab>
          <Tab>
            {formatMessage(
              { id: 'space.myItems.inactiveItems.title' },
              { amountOfNotStoredItems: visibleInactiveItems.length }
            )}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <StorageItemList
              groupByProductType
              items={activeItems}
              searchQuery={searchQuery}
              emptyStateBoxProps={activeItemsEmptyStateBoxProps}
            />
          </TabPanel>
          <TabPanel px={0}>
            <StorageItemList
              groupByProductType
              items={inactiveItems}
              hidePrice
              searchQuery={searchQuery}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  )
}
